import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { createColumnHelper } from '@tanstack/react-table';
import { StandaloneTable as Table, useTableState } from '@optimus-web/core';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { LocalizedText, useFeatureFlag, useLocalizeText } from '@mews/framework';
import { CheckInConfigurationType, CheckInConfigurationValue } from '@mews-commander/data-access';
import {
    CheckboxValues,
    CheckInCheckboxColumnName,
    CheckInFieldsTooltipValues,
    CheckInFieldsValues,
    ConfigurationEditableColumnValues,
} from '../types';
import { FieldData } from '../sections';
import { TabItemValue } from '../CheckInTabs/enums';
import { ChangeCheckboxesState, SetVisibilityData, TableDataItem } from './types';
import { LegallyRequired } from './LegallyRequired';
import { VisibilityAction } from './VisibilityAction';
import { createVisibilityCheckboxColumn } from './helpers';

interface CheckInFieldsTableProps {
    isLegallyRequiredRecord: Record<CheckInConfigurationType, boolean>;
    initialValues: CheckInFieldsValues;
    editableValues: ConfigurationEditableColumnValues;
    tooltipValues: CheckInFieldsTooltipValues;
    section: FieldData[];
    setVisibilityData: SetVisibilityData;
    visibilityData: CheckInFieldsValues;
    activeTabValue: TabItemValue;
    changeCheckboxesState: ChangeCheckboxesState;
    checkboxValues: CheckboxValues;
}

const columnHelper = createColumnHelper<TableDataItem>();

const originalColumns = [
    columnHelper.accessor('field', {
        header: () => <LocalizedText textKey={TextKey.CheckInFieldNameColumnHeader} />,
        enableSorting: false,
    }),
    columnHelper.accessor('isLegallyRequired', {
        header: () => <LocalizedText textKey={TextKey.CheckInFieldLegalRequirementColumnHeader} />,
        cell: LegallyRequired,
        enableSorting: false,
    }),
    columnHelper.accessor('visibility.reservationOwner', {
        header: () => <LocalizedText textKey={TextKey.CheckInFieldVisibilityColumnHeader} />,
        cell: (props) => <VisibilityAction {...props} columnName="reservationOwner" isChecked />,
        enableSorting: false,
    }),
];
interface ExtendedFieldsColumnOptions {
    changeCheckboxesState: (checkboxValues: CheckboxValues, changedColumn: CheckInCheckboxColumnName) => void;
    checkboxValues: CheckboxValues;
}

const getExtendedFieldsColumns = ({ changeCheckboxesState, checkboxValues }: ExtendedFieldsColumnOptions) => [
    columnHelper.accessor('field', {
        header: () => <LocalizedText textKey={TextKey.CheckInFieldNameColumnHeader} />,
        enableSorting: false,
    }),
    columnHelper.accessor('visibility.reservationOwner', {
        header: () => <LocalizedText textKey={TextKey.CheckInFieldReservationOwnerNameColumnHeader} />,
        cell: (props) => <VisibilityAction {...props} columnName="reservationOwner" isChecked />,
        enableSorting: false,
    }),
    createVisibilityCheckboxColumn({
        columnHelper,
        columnName: 'reservationAdult',
        setChecked: (value) =>
            changeCheckboxesState({ ...checkboxValues, reservationAdult: value }, 'reservationAdult'),
        isChecked: checkboxValues.reservationAdult,
    }),
    createVisibilityCheckboxColumn({
        columnHelper,
        columnName: 'reservationChild',
        setChecked: (value) =>
            changeCheckboxesState({ ...checkboxValues, reservationChild: value }, 'reservationChild'),
        isChecked: checkboxValues.reservationChild,
    }),
];

export const CheckInFieldsTable = ({
    initialValues,
    tooltipValues,
    editableValues,
    isLegallyRequiredRecord,
    section,
    activeTabValue,
    setVisibilityData,
    visibilityData,
    changeCheckboxesState,
    checkboxValues,
}: CheckInFieldsTableProps) => {
    const areExtendedHotelConfiguredFieldsEnabled = useFeatureFlag(FeatureFlag.extendedHotelConfiguredFields);

    const columns = areExtendedHotelConfiguredFieldsEnabled
        ? getExtendedFieldsColumns({
              changeCheckboxesState: (changedCheckboxValues, changedColumn) =>
                  changeCheckboxesState({
                      checkboxValues: changedCheckboxValues,
                      sourceTab: activeTabValue,
                      changedColumn,
                  }),
              checkboxValues,
          })
        : originalColumns;
    const [tableState, setTableState] = useTableState<TableDataItem>({
        pagination: { show: false },
        // Sorting will be implemented in kanban-6472
    });
    const localizeText = useLocalizeText();

    // this changes values in the form without actually changing it in the store - to remain history, when checkbox is being toggled
    const getVisibility = (type: CheckInConfigurationType) => ({
        reservationOwner: visibilityData[type].reservationOwner,
        reservationAdult: checkboxValues.reservationAdult
            ? visibilityData[type].reservationAdult
            : CheckInConfigurationValue.DoNotShow,
        reservationChild: checkboxValues.reservationChild
            ? visibilityData[type].reservationChild
            : CheckInConfigurationValue.DoNotShow,
    });

    const data: TableDataItem[] = pipe(
        section,
        array.filterMap(({ type, isDisabled, textKey }) =>
            initialValues[type] != null
                ? option.some({
                      type,
                      isDisabled,
                      tooltip: tooltipValues[type],
                      field: localizeText({ textKey }),
                      isEditable: {
                          reservationOwner: editableValues[type].reservationOwner,
                          reservationAdult: editableValues[type].reservationAdult,
                          reservationChild: editableValues[type].reservationChild,
                      },
                      isLegallyRequired: isLegallyRequiredRecord[type],
                      visibility: getVisibility(type),
                      setVisibilityData,
                  })
                : option.none
        )
    );

    return (
        <Table
            amountOfRows={data.length}
            columns={columns}
            data={data}
            onStateChange={setTableState}
            state={tableState}
            uniqueRowId="field"
        />
    );
};
